import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BrandMark } from '@/components/brand/BrandMark';

// Sign-in, register and password pages: the form on the left, and on wide
// screens a panel with the product (a real Latlens Maps screenshot).
export function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="grid min-h-[100dvh] bg-background lg:grid-cols-[1fr_1.05fr]">
      <div className="flex flex-col px-4 py-8 sm:px-10">
        <a href="https://latlens.com" className="self-start" aria-label="Latlens home">
          <BrandMark />
        </a>
        <main className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">{title}</h1>
          {subtitle && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </main>
        <footer className="flex gap-5 text-xs text-muted-foreground">
          <span>&copy; {new Date().getFullYear()} Latlens</span>
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
        </footer>
      </div>
      <aside className="relative hidden overflow-hidden bg-[#0B0C0E] lg:block" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(240,100,46,0.18),transparent_60%)]" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <p className="max-w-sm text-2xl font-semibold leading-snug tracking-tight text-[#F2F3F5]">
            Maps, search and routing for Africa, on one API key.
          </p>
          <img
            src="/auth-panel.jpg"
            alt=""
            className="mx-auto w-[320px] rounded-[28px] border border-white/10 shadow-[0_24px_64px_rgba(0,0,0,0.5)]"
          />
          <p className="text-sm text-[#A3A9B1]">Map data with an African bias, checked by local mappers.</p>
        </div>
      </aside>
    </div>
  );
}

/** An inline form error, read out when it appears. */
export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-xl border border-error/30 bg-error/10 px-3 py-2.5 text-sm text-error">
      {message}
    </p>
  );
}
